import { createSlice } from "@reduxjs/toolkit";

// Detect browser's preferred color scheme
export const getBrowserThemePreference = () => {
  if (typeof window !== "undefined" && window.matchMedia) {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  }
  return "light"; // Default fallback
};


export const getEffectiveThemeMode = (mode) => {
  if (mode === "system") {
    return getBrowserThemePreference();
  }
  return mode;
};

// Save theme to localStorage
const saveThemeToStorage = (themeData) => {
  try {
    localStorage.setItem("theme", JSON.stringify(themeData));
  } catch (e) {
    console.warn("Cannot save to localStorage:", e);
  }
};

// Load theme from localStorage or use browser preference
const getInitialTheme = () => {
  // Try localStorage
  const savedTheme = localStorage.getItem("theme");
  if (savedTheme) {
    try {
      const theme = JSON.parse(savedTheme);
      return theme;
    } catch (e) {
      console.error("Error parsing saved theme:", e);
    }
  }
  
  // If no saved theme, use browser's default preference
  const browserMode = getBrowserThemePreference();
  return {
    mode: browserMode,
    primaryColor: null,
    secondaryColor: null,
    customColors: {},
  };
};

const initialState = getInitialTheme();

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setThemeMode: (state, action) => {
      if (["light", "dark", "system"].includes(action.payload)) {
        state.mode = action.payload;
        saveThemeToStorage(state);
      }
    },
    setPrimaryColor: (state, action) => {
      state.primaryColor = action.payload;
      saveThemeToStorage(state);
    },
    setSecondaryColor: (state, action) => {
      state.secondaryColor = action.payload;
      saveThemeToStorage(state);
    },
    setCustomColor: (state, action) => {
      const { key, value } = action.payload;
      state.customColors[key] = value;
      saveThemeToStorage(state);
    },
    setCustomColors: (state, action) => {
      state.customColors = { ...state.customColors, ...action.payload };
      saveThemeToStorage(state);
    },
    resetTheme: (state) => {
      state.mode = "system";
      state.primaryColor = null;
      state.secondaryColor = null;
      state.customColors = {};
      saveThemeToStorage(state);
    },
    toggleThemeMode: (state) => {
      state.mode = state.mode === "light" ? "dark" : "light";
      saveThemeToStorage(state);
    },
    /**
     * Apply theme from another tab/window via the `storage` event.
     * Does not write localStorage again (the other tab already persisted).
     */
    hydrateThemeFromStorage: (state, action) => {
      const t = action.payload;
      if (!t || typeof t !== "object") {
        return;
      }
      if (["light", "dark", "system"].includes(t.mode)) {
        state.mode = t.mode;
      }
      state.primaryColor =
        t.primaryColor !== undefined && t.primaryColor !== ""
          ? t.primaryColor
          : null;
      state.secondaryColor =
        t.secondaryColor !== undefined && t.secondaryColor !== ""
          ? t.secondaryColor
          : null;
      state.customColors =
        t.customColors && typeof t.customColors === "object"
          ? { ...t.customColors }
          : {};
    },
  },
});

export const {
  setThemeMode,
  setPrimaryColor,
  setSecondaryColor,
  setCustomColor,
  setCustomColors,
  resetTheme,
  toggleThemeMode,
  hydrateThemeFromStorage,
} = themeSlice.actions;

export default themeSlice.reducer;


